/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import type { TabProps, Theme } from '@coursera/cds-core';
import {
  Link,
  Skeleton,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Tag,
  Typography,
  Typography2,
  breakpoints,
  useTheme,
} from '@coursera/cds-core';
import { css as cuiCss, placeholder } from '@coursera/coursera-ui';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import ClipsTranscriptContainer from 'bundles/clips/components/ClipsTranscriptContainer';
import { EnrollButton } from 'bundles/clips/components/EnrollButton';
import { PartnerLogo } from 'bundles/clips/components/PartnerLogo';
import type { ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import type { Course, OverridePartnerLogos, Partner, VLPItem } from 'bundles/clips/types/vlpSharedTypes';
import { getProgramHomeMiniXdpLink } from 'bundles/clips/utils/clipsUtils';
import getDurationFormatted from 'bundles/enterprise-legacy-learner-home/utils/getDurationFormatted';
import { Spacer } from 'bundles/enterprise-ui';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseMaterialItemsV2 from 'bundles/naptimejs/resources/onDemandCourseMaterialItems.v2';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/clips';

type PropsFromCaller = {
  course: Course;
  partners: Array<Partner> | null;
  overridePartnerLogos?: OverridePartnerLogos;
  videoItem: OnDemandCourseMaterialItemsV2;
  views?: number;
  skills: Array<string>;
  summary: string | null;
  durationMs: number;
  clipEngagementActions: JSX.Element;
  tracks: TrackList;
  lectureItems: Array<VLPItem>;
  accessProvidedViaProgram?: ProgramSuggestion;
  userEnrolledIntoCourse?: boolean;
  justEnrolledThroughMutation?: boolean;
  setShowEnrollmentButtonError: (error: boolean) => void;
};

type TabItem = TabProps & { 'data-testid'?: string; 'data-e2e'?: string };

export type Props = PropsFromCaller;

const TabNames = {
  About: 'ABOUT',
  Transcript: 'TRANSCRIPT',
  Summary: 'SUMMARY',
} as const;

export const clipMetadataContainer = () =>
  css({
    width: '100%',
  });

export const videoTitleContainer = (theme: Theme) =>
  css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--cds-spacing-100)',
    [breakpoints.down('xs')]: {
      display: 'none',
    },
  });

const courseInfoContainer = css({
  fontWeight: '600',
});

const partnerAndActionsContainer = css({
  display: 'flex',
  justifyContent: 'space-between',
});

const partnerContainer = (theme: Theme) =>
  css({
    [breakpoints.down('xs')]: {
      display: 'none',
    },
  });

const partnerLogoContainer = css({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const partnerLogoContainerMobile = (theme: Theme) =>
  css({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
    [breakpoints.up('sm')]: {
      display: 'none',
    },
  });

const actionsContainerDesktop = (theme: Theme) =>
  css({
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  });

const actionsContainerTablet = (theme: Theme) =>
  css({
    [breakpoints.down('sm')]: {
      display: 'block',
      marginTop: '16px',
      paddingBottom: '10px',
      marginBottom: '16px',
      borderBottom: `1px solid var(--cds-color-grey-50)`,
    },
    [breakpoints.up('md')]: {
      display: 'none',
    },
    [breakpoints.down('xs')]: {
      display: 'none',
    },
  });

const actionsContainerMobile = (theme: Theme) =>
  css({
    [breakpoints.down('xs')]: {
      display: 'block',
      paddingTop: '10px',
      marginTop: '16px',
      borderTop: `1px solid var(--cds-color-grey-50)`,
    },
    [breakpoints.up('sm')]: {
      display: 'none',
    },
  });

const spacer = css({
  margin: '0 4px',
});

const topicsContainer = (theme: Theme) =>
  css({
    [breakpoints.down('xs')]: {
      display: 'none',
    },
  });

const skillsList = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
});

const horizontalSpacer = css({
  margin: '0 4px',
});

const mobileMetadataLoadingContainer = (theme: Theme) =>
  css({
    [breakpoints.up('sm')]: {
      display: 'none',
    },
  });

const getTabs = ({ showSummary }: { showSummary: boolean }): TabItem[] => {
  return [
    {
      tab: {
        label: _t('About'),
        value: TabNames.About,
        'data-testid': 'about-tab',
      },
      display: true,
    },
    {
      tab: {
        label: _t('Transcript'),
        value: TabNames.Transcript,
        'data-testid': 'transcript-tab',
      },
      display: true,
    },
    {
      tab: {
        label: _t('Summary'),
        value: TabNames.Summary,
        'data-testid': 'summary-tab',
      },
      display: showSummary,
    },
  ]
    .filter((item) => item.display)
    .map((_) => _.tab);
};

export const ClipMetadata = ({
  partners,
  overridePartnerLogos,
  course,
  videoItem,
  clipEngagementActions,
  durationMs,
  views,
  skills,
  summary,
  tracks,
  lectureItems,
  accessProvidedViaProgram,
  userEnrolledIntoCourse,
  justEnrolledThroughMutation,
  setShowEnrollmentButtonError,
}: Props) => {
  const theme = useTheme();
  const partner = partners && partners[0];
  const formattedViews = views?.toLocaleString();
  const [currentTab, setCurrentTab] = useState<(typeof TabNames)[keyof typeof TabNames]>(
    summary ? TabNames.Summary : TabNames.About
  );
  const [tabs, setTabs] = useState<TabItem[]>([]);

  React.useEffect(() => {
    setTabs(getTabs({ showSummary: !!summary }));
  }, [summary]);

  const numCourseVideos = lectureItems.length;
  const totalCourseDuration = lectureItems.reduce((acc, lectureItem) => acc + lectureItem.timeCommitment, 0);
  const programHomeMiniXdpLink = getProgramHomeMiniXdpLink({
    courseId: course.id,
    accessProvidedViaProgram,
    trackingName: 'sfc_page_course_link',
    itemId: videoItem.id,
  });
  const isStandaloneCourse = course?.isStandaloneCourse;
  const trackEvent = useRetracked();
  let partnerId;
  let partnerName;
  let partnerSquareLogo;
  let logoSrc;

  if (partner) {
    partnerId = partner.id;
    partnerName = partner.name;
    partnerSquareLogo = partner.squareLogo;
    logoSrc = (overridePartnerLogos && overridePartnerLogos[partnerId]) || partnerSquareLogo;
  }

  const handleTabChange = (newValue: string) => {
    setCurrentTab(newValue as (typeof TabNames)[keyof typeof TabNames]);
    trackEvent({ trackingData: {}, trackingName: `lecture_tab_${newValue.toLowerCase()}`, action: 'click' });
  };

  return (
    <div data-testid="clip-metadata" css={clipMetadataContainer}>
      <div css={videoTitleContainer(theme)}>
        <Typography data-testid="sfc-video-title" variant="h1semibold">
          {videoItem.name}
        </Typography>
        {!isStandaloneCourse && (
          <EnrollButton
            enrollText={_t('Enroll in course')}
            goToCourseText={_t('Go to course')}
            userEnrolledIntoCourse={userEnrolledIntoCourse}
            justEnrolledThroughMutation={justEnrolledThroughMutation}
            course={course}
            itemId={videoItem.id}
            programId={accessProvidedViaProgram?.id}
            setShowEnrollmentButtonError={setShowEnrollmentButtonError}
          />
        )}
      </div>
      <Spacer size={8} />
      <div css={partnerAndActionsContainer}>
        <div css={partnerContainer(theme)}>
          <div css={partnerLogoContainer}>
            {partner && logoSrc && <PartnerLogo logoSrc={logoSrc} partnerId={partner.id} partnerName={partner.name} />}
            <Typography2 component="p" variant="bodySecondary" color="supportText">
              {partnerName}
            </Typography2>
          </div>
          {!isStandaloneCourse && (
            <>
              <Typography2 variant="bodySecondary" color="supportText" component="span" css={courseInfoContainer}>
                {_t('This video is part of: ')}
              </Typography2>
              <Link
                href={programHomeMiniXdpLink}
                variant="standard"
                typographyVariant="bodySecondary"
                target="_blank"
                component={TrackedA}
                trackingName="short_form_content_vlp_course_link"
                css={courseInfoContainer}
              >
                {course.name}
              </Link>
              <div>
                <Typography2 component="span" variant="bodySecondary" color="supportText">
                  {_t('#{numCourseVideos} videos', { numCourseVideos })}
                </Typography2>
                <span css={horizontalSpacer}>&#8226;</span>
                <Typography2 component="span" variant="bodySecondary" color="supportText">
                  {getDurationFormatted(totalCourseDuration)}
                </Typography2>
              </div>
            </>
          )}
        </div>

        <div css={actionsContainerDesktop(theme)}>{clipEngagementActions}</div>
      </div>
      <div css={actionsContainerTablet(theme)}>{clipEngagementActions}</div>
      <TabContext value={currentTab}>
        <Tabs
          aria-label={_t('Information about this clip and transcript tabs')}
          onChange={handleTabChange}
          variant="primary"
          css={{ width: 'fit-content' }}
        >
          {tabs.map((props) => (
            <Tab {...props} />
          ))}
        </Tabs>

        <TabPanel value={TabNames.About}>
          <Spacer size={16} />
          <div css={partnerLogoContainerMobile(theme)}>
            {partner && logoSrc && <PartnerLogo logoSrc={logoSrc} partnerId={partner.id} partnerName={partner.name} />}
            <Typography2 component="p" variant="bodyPrimary" color="supportText">
              {partnerName}
            </Typography2>
          </div>
          <Typography2 component="span" variant="bodyPrimary" color="supportText">
            {getDurationFormatted(durationMs)}
          </Typography2>
          <span css={spacer}>&#8226;</span>
          <Typography2 component="span" variant="bodyPrimary" color="supportText">
            {_t('#{formattedViews} views', { formattedViews })}
          </Typography2>
          <Spacer size={16} />
          <Typography2 component="h2" variant="subtitleMedium">
            {_t('Learning Objective')}
          </Typography2>
          <Spacer size={16} />
          <Typography2 component="p" variant="bodyPrimary">
            {course.description}
          </Typography2>
          <Spacer size={16} />
          {skills.length > 0 && (
            <div css={topicsContainer(theme)}>
              <Typography2 component="h2" variant="subtitleMedium">
                {_t('Topics')}
              </Typography2>
              <Spacer size={16} />
              <div css={skillsList}>
                {skills.map((skill) => (
                  <Tag variant="category" priority="primary" key={skill}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </TabPanel>
        <TabPanel value={TabNames.Transcript}>
          <ClipsTranscriptContainer courseId={course.id} itemId={videoItem.id} tracks={tracks} />
        </TabPanel>
        <TabPanel value={TabNames.Summary}>
          <Spacer size={16} />
          <Typography2 component="p" variant="bodyPrimary">
            {summary}
          </Typography2>
        </TabPanel>
      </TabContext>
      <div css={actionsContainerMobile(theme)}>{clipEngagementActions}</div>
    </div>
  );
};

export const ClipMetadataLoading = () => {
  const theme = useTheme();

  return (
    <div css={clipMetadataContainer} data-testid="clip-metadata-loading">
      <div css={videoTitleContainer(theme)}>
        <Skeleton variant="rect" width={150} height={36} className={cuiCss(placeholder.styles.shimmer).className} />
        <Skeleton variant="rect" width={150} height={48} className={cuiCss(placeholder.styles.shimmer).className} />
      </div>
      <Spacer size={8} />
      <div css={partnerAndActionsContainer}>
        <div css={partnerContainer(theme)}>
          <div css={partnerLogoContainer}>
            <Skeleton
              variant="circle"
              className={cuiCss(placeholder.styles.shimmer).className}
              width={20}
              height={20}
            />
            <Skeleton variant="rect" className={cuiCss(placeholder.styles.shimmer).className} width={200} height={20} />
          </div>
        </div>
      </div>
      <div css={mobileMetadataLoadingContainer(theme)}>
        <Skeleton
          variant="rect"
          height={48}
          className={cuiCss(placeholder.styles.shimmer).className}
          css={{ marginTop: '-8px', marginRight: '-16px', marginLeft: '-16px' }}
        />
        <Spacer size={32} />
        <div css={{ display: 'flex' }}>
          <Skeleton
            variant="rect"
            width={57}
            height={48}
            className={cuiCss(placeholder.styles.shimmer).className}
            css={{ marginRight: 32 }}
          />
          <Skeleton variant="rect" className={cuiCss(placeholder.styles.shimmer).className} width={85} height={48} />
        </div>
        <Spacer size={16} />
        <div css={partnerLogoContainer}>
          <Skeleton variant="rect" className={cuiCss(placeholder.styles.shimmer).className} width={20} height={20} />
          <Skeleton variant="rect" className={cuiCss(placeholder.styles.shimmer).className} width={200} height={20} />
        </div>
        <Spacer size={16} />
        {[...Array(15).keys()].map((_, idx) => (
          <Skeleton
            variant="text"
            className={cuiCss(placeholder.styles.shimmer).className}
            height={18}
            css={{ marginBottom: 8 }}
            key={idx}
          />
        ))}
      </div>
    </div>
  );
};
